import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import PageWrapper from '../PageWrapper/PageWrapper'
import aboutPageImage from '../../images/about-page.png'
import lilGrooversImage from '../../images/about-lil-groovers.png'
import chelseaImage from '../../images/chelsea.png'
import blackGroupImage from '../../images/black-group.png'
import DanceStyles from '../DanceStyles/DanceStyles'
import InfoPack from './InfoPack.pdf'
import './AboutPage.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSmile, faChartLine, faDownload, faUsers, faShirt, faTicket } from '@fortawesome/free-solid-svg-icons'

const AboutPage = () => {
  const location = useLocation()

  // Scroll to anchor if hash is present in URL
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash)
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    }
  }, [location])

  return (
    <PageWrapper title='About'>
      {/* Hero Section */}
      <section className='about-hero'>
        <div className='about-hero-image'>
          <img src={aboutPageImage} alt='CC Choreography Studio' />
        </div>
        <div className='about-hero-content'>
          <h1 className='about-hero-title'>Welcome to CC Choreography</h1>
          <p className='about-hero-text'>
            CC Choreography is a Newcastle dance studio welcoming everyone from beginners to
            experienced dancers, of all ages. Our Hamilton North and Lambton locations offer kids
            and teen classes in a safe, supportive environment where our teachers nurture a passion
            for dance.
          </p>
          <a href={InfoPack} download className='about-download-link'>
            <FontAwesomeIcon icon={faDownload} />
            <span>Download 2026 Information Pack</span>
          </a>
        </div>
      </section>

      {/* Values Section */}
      <section className='about-values'>
        <h2 className='about-section-title'>Our Core Values</h2>
        <div className='values-grid'>
          <div className='value-card'>
            <FontAwesomeIcon icon={faSmile} className='value-icon' />
            <h3>Happiness</h3>
            <p>To see our students smiling and laughing every day during dance class is priceless!</p>
          </div>
          <div className='value-card'>
            <FontAwesomeIcon icon={faChartLine} className='value-icon' />
            <h3>Growth</h3>
            <p>We strive for progress - not perfection, in a stress-free studio.</p>
          </div>
        </div>
      </section>

      {/* Studio Director Section */}
      <section className='about-director'>
        <div className='director-image'>
          <img src={chelseaImage} alt='Chelsea Cole - Studio Director' />
        </div>
        <div className='director-content'>
          <div className='director-header'>
            <h2 className='about-section-title'>Studio Director</h2>
          </div>
          <h3 className='director-name'>Chelsea Cole</h3>
          <div className='director-bio'>
            <p>
              Chelsea has 29 years dance experience and over 15 years teaching
              experience in Jazz, Contemporary, Lyrical, Tap, Hip Hop, Acro-dance,
              Musical Theatre, Baby Ballet/Jazz and Competitive Troupe Teams.
            </p>
            <p>
              Outside of the studio, she has also worked with multiple local schools
              teaching dance classes for sport, creating routines for the Hunter Dance
              Festival, running school dance groups and choreographing
              performance pieces for whole school Dance & Drama productions!
            </p>
            <p>
              Chelsea has a clear passion for working
              with children and teaching dance.
              She is a proud mum to three beautiful
              girls, Vivienne, Giselle & Celine and loves
              spending her spare time with family
              and close friends. Above all, she strives to
              be a positive role model and mentor for
              all her students and her three young
              daughters.
            </p>
          </div>
        </div>
      </section>

      {/* Our Team Section */}
      <section className='about-team-section'>
        <h2 className='about-section-title'>Meet Our Team</h2>
        <p className='about-section-intro'>
          Our passionate and experienced teachers are dedicated to inspiring and nurturing every dancer.
        </p>
        <div className='team-grid'>
          <div className='team-member'>
            <h3 className='team-member-name'>Chelsea Cole</h3>
            <p className='team-member-position'>Director, Teacher & Choreographer</p>
          </div>
          <div className='team-member'>
            <h3 className='team-member-name'>Kaleisha Brown</h3>
            <p className='team-member-position'>Teacher & Choreographer</p>
          </div>
          <div className='team-member'>
            <h3 className='team-member-name'>Steph Noble</h3>
            <p className='team-member-position'>Teacher & Choreographer</p>
          </div>
          <div className='team-member'>
            <h3 className='team-member-name'>Charlotte Connors</h3>
            <p className='team-member-position'>Teacher & Choreographer</p>
          </div>
          <div className='team-member'>
            <h3 className='team-member-name'>Kaitlyn Allen</h3>
            <p className='team-member-position'>Teacher & Choreographer</p>
          </div>
          <div className='team-member'>
            <h3 className='team-member-name'>Ava Globits</h3>
            <p className='team-member-position'>Teacher & Choreographer</p>
          </div>
        </div>
      </section>

      {/* Dance Styles Section */}
      <section className='about-styles-section'>
        <h2 className='about-section-title'>Our Dance Styles</h2>
        <p className='about-section-intro'>
          We offer a diverse range of dance styles to suit every interest and skill level.
        </p>
        <DanceStyles allowWrap={true} />
        <div className='styles-cta'>
          <p className='styles-cta-text'>
            For more information and details about our dance styles, download our information pack.
          </p>
          <a href={InfoPack} download className='styles-download-link'>
            <FontAwesomeIcon icon={faDownload} />
            <span>Download 2026 Information Pack</span>
          </a>
        </div>
        <div className='styles-image'>
          <img src={blackGroupImage} alt='Dance group' />
        </div>
      </section>

      {/* Tiny Tots Section */}
      <section className='about-tiny-tots'>
        <div className='tiny-tots-content'>
          <div className='tiny-tots-header'>
            <h2 className='about-section-title'>Lil Groovers - 2-5 years</h2>
          </div>
          <p className='tiny-tots-description'>
          We have had great success with our Lil Groovers Dance Program and welcome everyone to come and trial it to witness the magic for themselves! We share our love of dance in a welcoming, caring, fun and engaging lesson! We fill the room with music, movement & magical props whilst teaching ballet, jazz, balance, coordination & building their confidence!
          </p>
          <div className='tiny-tots-info-grid'>
            <div className='tiny-tots-info-item'>
              <FontAwesomeIcon icon={faUsers} className='tiny-tots-info-icon' />
              <p>Parents welcome to stay</p>
            </div>
            <div className='tiny-tots-info-item'>
              <FontAwesomeIcon icon={faShirt} className='tiny-tots-info-icon' />
              <p>Wear whatever you like</p>
            </div>
            <div className='tiny-tots-info-item'>
              <FontAwesomeIcon icon={faTicket} className='tiny-tots-info-icon' />
              <p>Concert performance is optional</p>
            </div>
          </div>
        </div>
        <div className='tiny-tots-image'>
          <img src={lilGrooversImage} alt='Lil Groovers Dance Program' />
        </div>
      </section>

      {/* Free Trial Section */}
      <section id='free-trial' className='about-free-trial'>
        <h2 className='about-section-title'>Free Trial for New Students</h2>
        <div className='free-trial-content'>
          <p className='free-trial-text'>
            We offer a free trial week of our dance classes to all new students or anyone wanting to try our dance studio! Please email Chelsea if you'd like to come along for a free trial week: <a href='mailto:chelsea@ccchoreography.org' className='free-trial-email'>chelsea@ccchoreography.org</a>
          </p>
        </div>
      </section>

      {/* Refer a Friend Section */}
      <section className='about-refer-friend'>
        <h2 className='about-section-title'>Refer a Friend</h2>
        <div className='refer-friend-content'>
          <p className='refer-friend-text'>
            Your word of mouth recommendation means the world to us! We have created an incentive for you as a thank you!
          </p>
          <p className='refer-friend-text'>
            You receive $30 off your term fees when your friend signs up to a term of dancing with us! There is no limit on this incentive - if you recommend us to 5 people and they all sign up, that's a $150 credit for you!
          </p>
        </div>
      </section>

    </PageWrapper>
  )
}

export default AboutPage
